import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { resolveSupabasePrivateAssignmentQr } from '../../features/runtime/supabaseAdapter';
import type { PrivateAssignmentQrResolutionV1 } from '../../features/qr/types';
import { getSupabaseClient } from '../../lib/supabase';

function promptText(prompt: unknown) {
  if (typeof prompt === 'string') return prompt;
  if (prompt && typeof prompt === 'object') {
    const value = (prompt as Record<string, unknown>).text ?? (prompt as Record<string, unknown>).prompt;
    if (typeof value === 'string') return value;
  }
  return 'Soal BelajarKu';
}

export function PrivateAssignmentResolverPage() {
  const { token = '' } = useParams();
  const location = useLocation();
  const client = useMemo(() => getSupabaseClient(), []);
  const [data, setData] = useState<PrivateAssignmentQrResolutionV1 | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [needsLogin, setNeedsLogin] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setData(null); setError(null); setNeedsLogin(false);
    void (async () => {
      if (!client) throw new Error('Supabase belum dikonfigurasi.');
      const session = await client.auth.getSession();
      if (!session.data.session) {
        if (!cancelled) setNeedsLogin(true);
        return;
      }
      try {
        const result = await resolveSupabasePrivateAssignmentQr(token);
        if (!cancelled) setData(result);
      } catch (cause) {
        if (!cancelled) setError(cause instanceof Error ? cause.message : 'Assignment QR tidak dapat dibuka.');
      }
    })().catch((cause) => { if (!cancelled) setError(cause instanceof Error ? cause.message : 'Assignment QR tidak dapat dibuka.'); });
    return () => { cancelled = true; };
  }, [client, token]);

  return (
    <main className="resolver-shell">
      <section className="card resolver-card">
        <div className="eyebrow">Private Assignment QR</div>
        <h1>Assignment BelajarKu</h1>
        {!data && !error && !needsLogin && <p className="muted">Memverifikasi QR dan hak akses…</p>}
        {needsLogin && (
          <div className="stack">
            <div className="feedback"><strong>Login diperlukan.</strong><p>QR private tidak membawa data pribadi dan hanya dapat dibuka oleh akun yang berhak.</p></div>
            <Link className="btn btn--primary" to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`}>Masuk untuk membuka assignment</Link>
          </div>
        )}
        {error && (
          <div className="feedback feedback--error">
            <strong>Assignment tidak dapat dibuka.</strong>
            <p>{error}</p>
            <p className="muted">Private QR membutuhkan akun BelajarKu yang berhak melihat siswa tersebut.</p>
          </div>
        )}
        {data && (
          <div className="stack">
            <div className="resource-summary"><span className="pill">{data.purpose}</span><span className="muted">{data.manifest.questions.length} soal frozen</span></div>
            <ol className="assignment-question-list">
              {data.manifest.questions.map((question) => <li key={question.questionInstanceId}><strong>{promptText(question.prompt)}</strong><span className="muted">{question.skill.code}</span></li>)}
            </ol>
            <div className="button-row">
              <Link className="btn btn--primary" to={`/print/assignment/${data.assignmentId}`}>Tampilan cetak</Link>
              <Link className="btn" to="/">Beranda</Link>
            </div>
          </div>
        )}
      </section>
    </main>
  );
}
